import axios, { type AxiosInstance } from "axios";

export default (axiosInstance: AxiosInstance) => {
  const pendingRequests = new Map<string, AbortController>();

  axiosInstance.interceptors.request.use(
    (config) => {
      if (!config.abortPreviousRequest) {
        return config;
      }

      if (!config.key) {
        throw new Error("Abort previous request requires a key");
      }

      const existingController = pendingRequests.get(config.key);
      if (existingController) {
        existingController.abort("Request aborted: New request initiated");
        pendingRequests.delete(config.key);
      }

      const controller = new AbortController();
      // eslint-disable-next-line no-param-reassign
      config.signal = controller.signal;
      pendingRequests.set(config.key, controller);

      return config;
    },
    (error) => Promise.reject(error)
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      if (!response.config.key) {
        return response;
      }

      pendingRequests.delete(response.config.key);
      return response;
    },
    (error) => {
      if (axios.isCancel(error)) {
        return Promise.reject(error);
      }

      if (error.config) {
        pendingRequests.delete(error.config.key);
      }

      return Promise.reject(error);
    }
  );

  const abortRequest = (key: string, reason = "Request aborted") => {
    const controller = pendingRequests.get(key);
    if (!controller) {
      return;
    }

    controller.abort(reason);
    pendingRequests.delete(key);
  };

  return { abortRequest };
};
